import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import IncomingCallModal from '@/components/IncomingCallModal';
import AddDeviceModal from '@/components/AddDeviceModal';
import CallButton from '../../components/family/calls/CallButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axiosClient from '../../api/axiosClient';
import SeniorsOverviewGrid from '../../components/family/dashboard/SeniorsOverviewGrid';
import DevicesStatusCard from '../../components/family/dashboard/DevicesStatusCard';
import DevicesManagementCard from '../../components/family/dashboard/DevicesManagementCard';
import ActivityMiniFeed from '../../components/family/dashboard/ActivityMiniFeed';
import { usePwaInstallPrompt } from '../../hooks/usePwaInstallPrompt';
import { useCallWebSocket } from '../../calls/useCallWebSocket.ts';
import { Phone, PhoneOff } from 'lucide-react';

/**
 * Family Home Dashboard - Modern Home Hub Design
 * 
 * Re-designed to feel like a modern smart home dashboard (Alexa/Google Home style)
 * with senior cards, device status, activity feed, and quick actions
 */
function FamilyDashboard() {
  const { familyUser, household, isAdmin, logout } = useFamilyAuth();
  const [seniors, setSeniors] = useState([]);
  const [devices, setDevices] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false);
  const wsRef = useRef(null);
  
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const isDev = process.env.NODE_ENV === 'development';
  
  // PWA Install Prompt
  const { isInstallable, promptInstall } = usePwaInstallPrompt();
  
  // Call WebSocket Hook for active call status
  const { currentCall } = useCallWebSocket(familyUser?.id);

  useEffect(() => {
    const loadData = async () => {
      if (!household) {
        console.warn('[Dashboard] No household available yet');
        return;
      }
      
      console.log('[Dashboard] Loading data for household:', household.id);
      setLoading(true);
      setLoadError(null);
      
      try {
        const [seniorsRes, devicesRes, activityRes] = await Promise.all([
          axiosClient.get(`/api/households/${household.id}/seniors`).catch(err => {
            console.error('[Dashboard] Failed to load seniors:', err);
            throw err;
          }),
          axiosClient.get(`/api/households/${household.id}/devices`).catch(err => {
            console.error('[Dashboard] Failed to load devices:', err);
            throw err;
          }),
          axiosClient.get(`/api/activity/${household.id}?limit=5`).catch(err => {
            console.error('[Dashboard] Failed to load activity (non-critical):', err);
            return { data: { activities: [] } }; // Non-critical failure
          })
        ]);
        
        console.log('[Dashboard] ✅ Data loaded successfully:', {
          seniors: seniorsRes.data.length,
          devices: devicesRes.data.length,
          activities: activityRes.data?.activities?.length || 0
        });
        
        setSeniors(seniorsRes.data || []);
        setDevices(devicesRes.data || []);
        setRecentActivity(activityRes.data?.activities || []);
      } catch (error) {
        console.error('❌ [Dashboard] Fehler beim Laden der Dashboard-Daten:', error);
        
        // Determine error type
        let errorMsg = 'Fehler beim Laden der Dashboard-Daten.';
        if (error.response) {
          if (error.response.status === 401 || error.response.status === 403) {
            errorMsg = 'Sitzung abgelaufen. Bitte neu anmelden.';
          } else if (error.response.status >= 500) {
            errorMsg = 'Server-Fehler. Bitte später erneut versuchen.';
          } else if (error.message && error.message.includes('CORS')) {
            errorMsg = 'Verbindungsfehler (CORS). Bitte Seite neu laden.';
          }
        } else if (error.message === 'Network Error') {
          errorMsg = 'Keine Verbindung zum Server. Internetverbindung prüfen.';
        }
        
        setLoadError(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [household]);

  // Live Status Updates: Poll device status every 30 seconds
  useEffect(() => {
    if (!household) return;
    
    const refreshDeviceStatus = async () => {
      try {
        const devicesRes = await axiosClient.get(`/api/households/${household.id}/devices`);
        setDevices(devicesRes.data || []);
        console.log('[Dashboard] 🔄 Device status refreshed');
      } catch (error) {
        console.error('[Dashboard] Failed to refresh device status:', error);
        // Silent failure - don't show error to user
      }
    };
    
    // Start polling after initial load
    if (!loading) {
      const pollInterval = setInterval(refreshDeviceStatus, 30000); // Every 30 seconds
      
      return () => {
        clearInterval(pollInterval);
      };
    }
  }, [household, loading]);


  // Reload devices after successful pairing
  const handleDevicePaired = async (pairedDevice) => {
    console.log('✅ Device paired successfully:', pairedDevice);
    
    // Reload devices list
    try {
      const devicesRes = await axiosClient.get(`/api/households/${household.id}/devices`);
      setDevices(devicesRes.data);
    } catch (error) {
      console.error('Error reloading devices:', error);
    }
  };

  // WebSocket for incoming calls - with Auto-Retry & Backoff
  useEffect(() => {
    if (!familyUser?.id) return;

    let retryTimeout = null;
    let retryCount = 0;
    const MAX_RETRIES = 5;
    const RETRY_DELAYS = [2000, 5000, 10000, 20000, 30000];
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    const connectWebSocket = () => {
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }

      const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${WS_URL}/api/ws/call/family/${familyUser.id}`);
      
      ws.onopen = () => {
        if (isLocalhost) {
          console.log('✅ Family WebSocket connected');
        }
        retryCount = 0;
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (isLocalhost) {
          console.log('📨 Family received:', data);
        }

        if (data.type === 'incoming_call') {
          setIncomingCall(data);
        }
      };

      ws.onerror = () => {
        // Silently handle errors
      };

      ws.onclose = (event) => {
        const isExpectedError = event.code === 1005 || event.code === 1006;
        
        if (!isExpectedError && isLocalhost) {
          console.log(`📞 Family WebSocket closed (code: ${event.code}, reason: ${event.reason || 'none'})`);
        }
        
        if (event.code !== 1000 && retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAYS[retryCount] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
          
          retryTimeout = setTimeout(() => {
            retryCount++;
            connectWebSocket();
          }, delay);
        } else if (retryCount >= MAX_RETRIES && isLocalhost) {
          console.error('⚠️ Max WebSocket retry attempts reached. Connection unavailable.');
        }
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [familyUser?.id, BACKEND_URL]);

  const handleCallAnswer = (accepted) => {
    if (accepted && incomingCall?.session_id) {
      navigate(`/family/video-call/${incomingCall.session_id}`);
    }
    setIncomingCall(null);
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2C2640] via-[#1a1625] to-[#0f0a1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-[#6DCEE8] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white text-lg">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  // Helper: Get device and senior info for active call
  const getCallInfo = () => {
    if (!currentCall) return null;
    
    const device = devices.find(d => d.id === currentCall.deviceId);
    const senior = seniors.find(s => s.id === currentCall.seniorId);
    
    return {
      device,
      senior,
      seniorName: senior?.name || 'Unbekannt'
    };
  };
  
  const callInfo = getCallInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2C2640] via-[#1a1625] to-[#0f0a1a] pb-24">
      {/* Incoming Call Modal */}
      {incomingCall && (
        <IncomingCallModal
          caller={incomingCall.caller_name}
          onAccept={() => handleCallAnswer(true)}
          onDecline={() => handleCallAnswer(false)}
        />
      )}

      {/* Add Device Modal */}
      <AddDeviceModal
        isOpen={isAddDeviceModalOpen}
        onClose={() => setIsAddDeviceModalOpen(false)}
        seniors={seniors}
        onSuccess={handleDevicePaired}
      />
      
      {/* Error Banner */}
      {loadError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
            <span className="text-2xl">❌</span>
            <div className="flex-1">
              <h3 className="text-red-300 font-semibold mb-1">Fehler beim Laden</h3>
              <p className="text-red-400 text-sm">{loadError}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Neu laden
            </button>
          </div>
        </div>
      )}
      
      {/* Active Call Status Banner */}
      {currentCall && (currentCall.status === 'ringing' || currentCall.status === 'in_call') && callInfo && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-gradient-to-r from-[#6DCEE8]/20 to-[#5B4B8A]/20 border border-[#6DCEE8]/40 rounded-xl p-4 flex items-center justify-between backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Phone className="w-6 h-6 text-[#6DCEE8]" />
                {currentCall.status === 'ringing' && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6DCEE8] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#6DCEE8]"></span>
                  </span>
                )}
              </div>
              <div>
                <p className="text-white font-semibold">
                  {currentCall.status === 'ringing' ? 'Anruf wird gestartet...' : 'Aktiver Anruf'}
                </p>
                <p className="text-neutral-300 text-sm">
                  {callInfo.seniorName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                currentCall.status === 'ringing' 
                  ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                  : 'bg-green-500/20 text-green-300 border border-green-500/40'
              }`}>
                {currentCall.status === 'ringing' ? 'Klingelt...' : 'Verbunden'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Left: Welcome */}
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                Willkommen zurück, {familyUser?.name?.split(' ')[0] || 'Benutzer'}! 👋
              </h1>
              <div className="flex items-center gap-3">
                <p className="text-neutral-300">{household?.name}</p>
                {isAdmin && (
                  <span className="px-3 py-1 bg-[#F6B352]/20 text-[#F6B352] rounded-full text-xs font-semibold border border-[#F6B352]/40">
                    Admin
                  </span>
                )}
              </div>
            </div>

            {/* Right: Quick Actions */}
            <div className="flex items-center gap-3">
              <CallButton seniors={seniors} devices={devices} />
              
              {isAdmin && (
                <button
                  onClick={() => setIsAddDeviceModalOpen(true)}
                  className="px-6 py-3 bg-neutral-700 text-white rounded-xl font-semibold hover:bg-neutral-600 transition-colors"
                >
                  📱 Gerät koppeln
                </button>
              )}

              {/* PWA Install Button */}
              {isInstallable && (
                <button
                  onClick={promptInstall}
                  className="px-6 py-3 bg-gradient-to-r from-[#F6B352] to-[#5B4B8A] text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
                  title="Hayat Family als App installieren"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  App installieren
                </button>
              )}

              {/* Admin Dropdown Menu */}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-3 bg-neutral-800 text-white rounded-xl hover:bg-neutral-700 transition-colors">
                      ⚙️
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Admin-Tools</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem 
                      onClick={() => navigate('/family/admin/members')}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">👥</span>
                      Mitglieder verwalten
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => navigate('/family/admin/seniors')}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">👴</span>
                      Senioren verwalten
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => navigate('/family/admin/settings')}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">⚙️</span>
                      Einstellungen
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => navigate('/family/admin/activity')}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">📊</span>
                      Aktivitätsbericht
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => setIsAddDeviceModalOpen(true)}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">📱</span>
                      Gerät verbinden
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem 
                      onClick={async () => {
                        if (window.confirm('Sind Sie sicher, dass Sie sich abmelden möchten?')) {
                          await logout();
                          navigate('/family/login');
                        }
                      }}
                      className="cursor-pointer text-red-600"
                    >
                      <span className="mr-2">🚪</span>
                      Abmelden
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Non-admin logout */}
              {!isAdmin && (
                <button
                  onClick={async () => {
                    if (window.confirm('Sind Sie sicher, dass Sie sich abmelden möchten?')) {
                      await logout();
                      navigate('/family/login');
                    }
                  }}
                  className="p-3 bg-neutral-800 text-white rounded-xl hover:bg-neutral-700 transition-colors"
                  title="Abmelden"
                >
                  🚪
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {loadError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-500/20 border border-red-500/40 text-red-200 px-6 py-4 rounded-xl flex items-start gap-4">
            <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Fehler beim Laden der Dashboard-Daten</h3>
              <p className="text-sm">{loadError}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
              >
                Seite neu laden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Overlay (Dev Mode Only) */}
      {isDev && household && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="bg-yellow-500/20 border border-yellow-500/40 text-yellow-200 px-4 py-3 rounded-lg text-xs font-mono">
            <div className="font-bold mb-1">🔍 Debug Info (Dev Mode):</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>Household: <span className="font-bold">{household.id.slice(0, 8)}...</span></div>
              <div>Seniors: <span className="font-bold">{seniors.length}</span></div>
              <div>Devices: <span className="font-bold">{devices.length}</span></div>
              <div>Status: <span className="font-bold">{loading ? 'loading' : loadError ? 'error' : 'success'}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left: Seniors Overview (Main Section) */}
          <div className="lg:col-span-8 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Deine Senioren</h2>
              <SeniorsOverviewGrid 
                seniors={seniors}
                devices={devices}
                household={household}
                currentCall={currentCall}
              />
            </div>
          </div>

          {/* Right: Sidebar (Devices & Activity) */}
          <div className="lg:col-span-4 space-y-6">
            <DevicesStatusCard 
              devices={devices}
              seniors={seniors}
              isAdmin={isAdmin}
              currentCall={currentCall}
              onDeviceUpdated={handleDevicePaired}
            />
            
            <ActivityMiniFeed 
              activities={recentActivity}
              isAdmin={isAdmin}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default FamilyDashboard;
